// ComprasPDFButton.tsx
import React, { useEffect, useState } from 'react';
import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { BlobProvider } from '@react-pdf/renderer';
import CompraOrderPDF from './ComprasOrderPDF';
import { ICompra } from '../../types/compras.types';
import comprasApi, { IArticuloDetalle } from '../../../services/api/comprasApi';

interface ComprasPDFButtonProps {
    compra: ICompra;
}

const ComprasPDFButton: React.FC<ComprasPDFButtonProps> = ({ compra }) => {
    const [loading, setLoading] = useState(false);
    const [pdfData, setPdfData] = useState<any>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [blob, setBlob] = useState<Blob | null>(null);

    useEffect(() => {
        if (blob) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Orden_Compra_${compra.OrdenCompra}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setBlob(null);
            setPdfData(null);
        }
    }, [blob, compra.OrdenCompra]);

    const transformCompraForPDF = async (compra: ICompra) => {
        if (!compra.Id) {
            throw new Error('ID de compra no definido');
        }

        const detalleCompleto = await comprasApi.getDetalleCompleto(compra.Id);
        
        return {
            OrdenCompra: detalleCompleto.compra.OrdenCompra,
            FechaSolicitud: detalleCompleto.compra.FechaSolicitud,
            FechaEntrega: detalleCompleto.compra.FechaEntrega || '',
            empresa: {
                Nombre: 'NOMBRE DE LA EMPRESA',
                NIT: 'NIT DE LA EMPRESA',
                Direccion: 'DIRECCIÓN DE LA EMPRESA'
            },
            proveedor: {
                Nombre: detalleCompleto.compra.proveedor.Nombre,
                NumeroIdentificacion: detalleCompleto.compra.proveedor.NumeroIdentificacion,
                Direccion: detalleCompleto.compra.proveedor.Direccion,
                Telefono: detalleCompleto.compra.proveedor.Telefono,
                RepresentanteLegal: detalleCompleto.compra.proveedor.RepresentanteLegal
            },
            articulosCompra: detalleCompleto.articulosCompra.map((articulo: IArticuloDetalle) => ({
                articulo: {
                    Nombre: articulo.articulo.Nombre,
                    Descripcion: articulo.articulo.CodigoEAN || ''
                },
                CantidadComprada: Number(articulo.CantidadComprada),
                PrecioCompra: Number(articulo.PrecioCompra),
                Total: Number(articulo.CantidadComprada) * Number(articulo.PrecioCompra)
            })),
            estadoCompra: {
                Nombre: detalleCompleto.compra.estadoCompra?.Nombre || ''
            },
            usuario: {
                Usuario: detalleCompleto.compra.usuario?.Usuario || ''
            }
        };
    };

    const handleDownload = async (blob: Blob) => {
        if (isDownloading) return; // Evitar múltiples descargas
        
        try {
            setIsDownloading(true);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Orden_Compra_${compra.OrdenCompra}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setPdfData(null); // Limpiar los datos después de la descarga
        } finally {
            setIsDownloading(false);
        }
    };

    const handlePrint = async () => {
        try {
            setLoading(true);
            const datos = await transformCompraForPDF(compra);
            setPdfData(datos);
        } catch (error) {
            console.error('Error al generar PDF:', error);
            alert('Error al generar el PDF: ' + (error as Error).message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {!pdfData ? (
                <Tooltip title="Generar PDF">
                    <IconButton
                        onClick={handlePrint}
                        color="primary"
                        size="small"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : <PdfIcon />}
                    </IconButton>
                </Tooltip>
            ) : (
                <BlobProvider document={<CompraOrderPDF compra={pdfData} />}>
                    {({ blob: pdfBlob, loading: pdfLoading, error }) => {
                        if (pdfLoading) {
                            return (
                                <IconButton color="primary" size="small" disabled>
                                    <CircularProgress size={24} />
                                </IconButton>
                            );
                        }

                        if (error) {
                            console.error('Error generando PDF:', error);
                            setPdfData(null);
                            return null;
                        }

                        if (pdfBlob && !blob) {
                            setBlob(pdfBlob);
                            return (
                                <IconButton color="primary" size="small" disabled>
                                    <CircularProgress size={24} />
                                </IconButton>
                            );
                        }

                        return null;
                    }}
                </BlobProvider>
            )}
        </>
    );
};

export default ComprasPDFButton;